import { DOCUMENT, registerLocaleData } from '@angular/common';
import { Component, EventEmitter, HostListener, Inject, LOCALE_ID, OnInit, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';

import { LoadingBarModule } from '@ngx-loading-bar/core';
import { LoadingBarHttpClientModule } from "@ngx-loading-bar/http-client";
import { LoadingBarRouterModule } from '@ngx-loading-bar/router';
import { TranslateService } from '@ngx-translate/core';
import { PrimeNGConfig } from 'primeng/api';

import { AppService } from '../../../services/app.service';
import { MessageService } from '../../../services/message.service';
import { SystemService } from '../../../services/system.service';
import { UserService } from '../../../services/user.service';

import { DialogOption } from '../../../models/dialog-option.model';

import { AlertEnum, DialogEnum, OptionEnum } from '../../../enumerators/messages.enum';

import locales from '@angular/common/locales/es-MX';
import { InactivityAlertComponent } from '../../../components/inactivity-alert/inactivity-alert.component';
import { MessageComponent } from '../../../components/messages/message/message.component';

import '@angular/localize/init';


registerLocaleData(locales, 'es');

@Component({
  selector: 'app-main',
  encapsulation: ViewEncapsulation.None,
  templateUrl: './main.component.html',
  providers: [{ provide: LOCALE_ID, useValue: 'es' }],
  styleUrls: ['./main.component.scss'],
  standalone: true,
  imports: [LoadingBarModule, LoadingBarHttpClientModule, LoadingBarRouterModule, RouterOutlet, MessageComponent, InactivityAlertComponent]
})
export class MainComponent implements OnInit {

  @ViewChild('message') messageComponent!: MessageComponent;

  @Output() onClick: EventEmitter<any> = new EventEmitter<any>();

  @Output() onResize: EventEmitter<any> = new EventEmitter<any>();

  @HostListener('window:mousemove') userMouseMove() {
    this.clearInactivityTimer();
    this.setTimeout();
    // Notificar actividad del usuario
    this.appService.userActivity.next(Date.now());
  }

  @HostListener('document:tap') userTap() {
    this.clearInactivityTimer();
    this.setTimeout();
    // Notificar actividad del usuario
    this.appService.userActivity.next(Date.now());
  }

  @HostListener('document:click') userClick(event: any) {
    this.clearInactivityTimer();
    this.setTimeout();
    // Notificar actividad del usuario
    this.appService.userActivity.next(Date.now());

    this.onClick.emit(event);
  }

  @HostListener('document:mousewheel') userScroll() {
    this.clearInactivityTimer();
    this.setTimeout();
    // Notificar actividad del usuario
    this.appService.userActivity.next(Date.now());
  }

  @HostListener('document:keydown') userKey() {
    this.clearInactivityTimer();
    this.setTimeout();
    // Notificar actividad del usuario
    this.appService.userActivity.next(Date.now());
  }

  @HostListener('window:resize', ['$event'])
  private onListenerResize(event: any): void {
    this.onResize.emit(event);
  }


  userActivity: any;
  alertatimerIniciada: boolean = false;
  showInactivityAlert: boolean = false;

  constructor(public appService: AppService, private systemService: SystemService, @Inject(DOCUMENT) private document: Document, private router: Router, private translateService: TranslateService, private userService: UserService, private messageService: MessageService, private configNg: PrimeNGConfig) {
    let themeLink = this.document.getElementById('app-theme') as HTMLLinkElement; // Corregido: app-theme (sin 's')
    const tema: string | null = localStorage.getItem('tema');

    if (themeLink) {
      themeLink.href = tema ? tema : "dreamsolution-dark.css"; // Cambiado a dreamsolution-dark.css como tema por defecto
    }
    router.events.subscribe(value => {
      if (value instanceof NavigationEnd) {
        this.systemService.activateOptionMenu(value.urlAfterRedirects);
      }
    });

    this.appService.startIdleCount.subscribe((value) => {
      if (value) {
        this.setTimeout();
      }
    });

    this.appService.sessionEnded.subscribe((value) => {
      if (value) {
        // Limpiar todos los timers cuando la sesión termina
        this.clearInactivityTimer();
        if (this.alertatimerIniciada) {
          this.messageComponent.closeAlertTimer();
          this.alertatimerIniciada = false;
        }
        this.showResumeSession();
      }
    });

    configNg.ripple = true;
  }

  ngOnInit() {
    this.translateService.get('primeng').subscribe(res => this.configNg.setTranslation(res));
  }

  setTimeout() {
    if (localStorage.getItem(this.systemService.localStoreVariable.JWT) && this.systemService.user?.userType?.inactivityTime) {

      // Solo cerrar la alerta si está iniciada y el usuario interactuó
      if (this.alertatimerIniciada) {
        this.messageComponent.closeAlertTimer();
        this.alertatimerIniciada = false;
      }

      const inactivityTimeMs = this.systemService.user.userType?.inactivityTime!;

      // Si el tiempo de inactividad es menor a 2 minutos, mostrar alerta inmediatamente después de la mitad del tiempo
      // Si es mayor a 2 minutos, mostrar alerta 1 minuto antes
      let tiempoAlerta: number;

      if (inactivityTimeMs <= (2 * 60 * 1000)) {
        // Para tiempos <= 2 minutos, mostrar alerta a la mitad del tiempo
        tiempoAlerta = Math.floor(inactivityTimeMs / 2);
      } else {
        // Para tiempos > 2 minutos, mostrar alerta 1 minuto antes
        tiempoAlerta = inactivityTimeMs - (60 * 1000);
      }

      // Asegurar que el tiempo de alerta sea al menos 1 segundo
      tiempoAlerta = Math.max(tiempoAlerta, 1000);

      this.userActivity = setTimeout(() => {
        // Verificar que no se haya cerrado la sesión mientras esperaba y que no haya otra alerta activa
        if (localStorage.getItem(this.systemService.localStoreVariable.JWT) && !this.alertatimerIniciada) {
          this.alertatimerIniciada = true;
          // Mostrar el nuevo componente de alerta de inactividad
          this.showInactivityAlert = true;
        }
      }, tiempoAlerta);
    }
  }

  // Método para limpiar el timer de inactividad
  clearInactivityTimer() {
    if (this.userActivity) {
      clearTimeout(this.userActivity);
      this.userActivity = null;
    }
    // Si hay una alerta de inactividad activa, ocultarla
    if (this.showInactivityAlert) {
      this.showInactivityAlert = false;
      this.alertatimerIniciada = false;
    }
  }

  // Método llamado cuando el usuario decide continuar la sesión
  onContinueSession() {
    this.showInactivityAlert = false;
    this.alertatimerIniciada = false;
    // Reiniciar el timer de inactividad
    this.setTimeout();
  }

  // Método llamado cuando se agota el tiempo de la alerta
  onInactivityTimeout() {
    this.showInactivityAlert = false;
    this.alertatimerIniciada = false;
    // Cerrar sesión por inactividad (sin petición al backend ya que el token puede estar expirado)
    this.messageService.alert(AlertEnum.Warning, this.translateService.instant("TiempoInactividad"), false);
    this.userService.logOutByExpiration();
  }

  showResumeSession() {
    if (localStorage.getItem(this.systemService.localStoreVariable.JWT)) {
      let options = [];

      options.push(new DialogOption({ title: this.translateService.instant("SesionFinalizado.Si"), value: 1 }));
      options.push(new DialogOption({ title: this.translateService.instant("SesionFinalizado.No"), value: 2, type: OptionEnum.Negative }));

      this.messageService.dialog(DialogEnum.Advise, "", this.translateService.instant("SesionFinalizado.Mensaje"), options).then((response: any) => {
        if (response) {
          this.userService.resumeSession();
        }
        else {
          // Usar el método específico para cerrar sesión por expiración
          this.userService.logOutByExpiration();
        }
      });
    }
  }
}
